import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Table, TableHead, TableBody,
    TableRow, TableCell, TextField, Chip, IconButton, CircularProgress, Paper
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { TaxService } from '../../services/tax.service';
import { ITaxYear, ITaxSlab } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

const TaxSlabList: React.FC = () => {
    const { t } = useTranslation(['tax', 'common']);
    const { showSuccess, showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([]);
    const [selectedYearId, setSelectedYearId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showNewYearForm, setShowNewYearForm] = useState(false);
    const [newYear, setNewYear] = useState({ label: '', startDate: '', endDate: '' });

    const load = () => {
        TaxService.getTaxYears(institutionId)
            .then(years => {
                setTaxYears(years);
                if (!selectedYearId && years.length > 0) {
                    const active = years.find(y => y.isActive) ?? years[0];
                    setSelectedYearId(active.id!);
                }
            })
            .catch(() => showError(t('shared.loadTaxYearsFailed')))
            .finally(() => setLoading(false));
    };

    useEffect(() => { load(); }, [institutionId]);

    const selectedYear = taxYears.find(y => y.id === selectedYearId);

    const updateSlab = (idx: number, field: keyof ITaxSlab, value: string) => {
        if (!selectedYear) return;
        const slabs = [...selectedYear.slabs];
        slabs[idx] = { ...slabs[idx], [field]: value === '' ? undefined : parseFloat(value) };
        setTaxYears(years => years.map(y => y.id === selectedYearId ? { ...y, slabs } : y));
    };

    const addRow = () => {
        if (!selectedYear) return;
        const slabs = [...selectedYear.slabs, { incomeFrom: 0, rate: 0 }];
        setTaxYears(years => years.map(y => y.id === selectedYearId ? { ...y, slabs } : y));
    };

    const removeRow = (idx: number) => {
        if (!selectedYear) return;
        const slabs = selectedYear.slabs.filter((_, i) => i !== idx);
        setTaxYears(years => years.map(y => y.id === selectedYearId ? { ...y, slabs } : y));
    };

    const saveSlabs = async () => {
        if (!selectedYear) return;
        setSaving(true);
        try {
            await TaxService.saveTaxYear({ ...selectedYear, institutionId });
            showSuccess(t('slabs.toast.saved'));
            load();
        } catch {
            showError(t('slabs.toast.saveFailed'));
        } finally { setSaving(false); }
    };

    const activate = async (id: string) => {
        await TaxService.activateTaxYear(id, institutionId);
        load();
    };

    const createYear = async () => {
        await TaxService.saveTaxYear({ ...newYear, institutionId, slabs: [] });
        setShowNewYearForm(false);
        setNewYear({ label: '', startDate: '', endDate: '' });
        load();
    };

    if (loading) return <CircularProgress />;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">{t('slabs.title')}</Typography>
                <Button variant="outlined" onClick={() => setShowNewYearForm(!showNewYearForm)}>
                    {t('slabs.newYear')}
                </Button>
            </Box>

            {showNewYearForm && (
                <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <TextField size="small" label={t('slabs.labelField')} value={newYear.label}
                        onChange={e => setNewYear(n => ({ ...n, label: e.target.value }))} />
                    <TextField size="small" label={t('slabs.startDate')} type="date" InputLabelProps={{ shrink: true }}
                        value={newYear.startDate} onChange={e => setNewYear(n => ({ ...n, startDate: e.target.value }))} />
                    <TextField size="small" label={t('slabs.endDate')} type="date" InputLabelProps={{ shrink: true }}
                        value={newYear.endDate} onChange={e => setNewYear(n => ({ ...n, endDate: e.target.value }))} />
                    <Button variant="contained" size="small" onClick={createYear}>{t('common:actions.create')}</Button>
                </Paper>
            )}

            <Box display="flex" gap={1} mb={3} flexWrap="wrap">
                {taxYears.map(y => (
                    <Chip key={y.id}
                        label={y.isActive ? t('slabs.activeSuffix', { label: y.label }) : y.label}
                        color={y.id === selectedYearId ? 'primary' : 'default'}
                        variant={y.id === selectedYearId ? 'filled' : 'outlined'}
                        onClick={() => setSelectedYearId(y.id!)}
                        onDelete={!y.isActive ? () => activate(y.id!) : undefined}
                        deleteIcon={!y.isActive ? <span style={{ fontSize: 10, padding: '0 4px' }}>{t('slabs.setActive')}</span> : undefined}
                    />
                ))}
            </Box>

            {selectedYear && (
                <Paper>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ background: '#f1f5f9' }}>
                                <TableCell>#</TableCell>
                                <TableCell>{t('slabs.columns.incomeFrom')}</TableCell>
                                <TableCell>{t('slabs.columns.incomeTo')}</TableCell>
                                <TableCell>{t('slabs.columns.rate')}</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {selectedYear.slabs.map((slab, idx) => (
                                <TableRow key={idx}>
                                    <TableCell>{idx + 1}</TableCell>
                                    <TableCell>
                                        <TextField size="small" type="number"
                                            value={slab.incomeFrom ?? ''}
                                            onChange={e => updateSlab(idx, 'incomeFrom', e.target.value)} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField size="small" type="number"
                                            placeholder="∞"
                                            value={slab.incomeTo ?? ''}
                                            onChange={e => updateSlab(idx, 'incomeTo', e.target.value)} />
                                    </TableCell>
                                    <TableCell>
                                        <TextField size="small" type="number"
                                            value={slab.rate ?? ''}
                                            onChange={e => updateSlab(idx, 'rate', e.target.value)} />
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" onClick={() => removeRow(idx)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <Box p={2} display="flex" gap={2} alignItems="center">
                        <Button size="small" onClick={addRow}>{t('slabs.addRow')}</Button>
                        <Button variant="contained" size="small" onClick={saveSlabs} disabled={saving}>
                            {saving ? t('shared.saving') : t('slabs.save')}
                        </Button>
                    </Box>
                </Paper>
            )}
        </Box>
    );
};

export default TaxSlabList;
